import React from "react";
import { useFetcher } from "@remix-run/react";
// Removed useAppBridge hook to prevent standalone hub crashes
// import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Box,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Button,
  Modal,
  TextField,
} from "@shopify/polaris";
import { STARTER_PLAN } from "../constants";


export function DangerZoneCard({ 
  isDemoMode, 
  isReadOnly = false,
  recordCount = 0, 
  sessionShop, 
  shopHandle, 
  planName, 
  appEnv,
  onDownload, 
  isDownloading 
}: { 
  isDemoMode?: boolean, 
  isReadOnly?: boolean,
  recordCount?: number, 
  sessionShop?: string, 
  shopHandle?: string, 
  planName: string, 
  appEnv: string,
  onDownload: () => void, 
  isDownloading: boolean 
}) {
  const fetcher = useFetcher<any>();
  const isDeleting = fetcher.state !== "idle";
  const error = fetcher.data?.success === false ? fetcher.data.error : null;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState("");

  const shopify = typeof window !== "undefined" ? (window as any).shopify : null;

  React.useEffect(() => {
    if (fetcher.data?.success) {
      setModalOpen(false);
      setConfirmText("");
      if (shopify?.toast) {
        shopify.toast.show(fetcher.data.message || "App data reset successfully.");
      } else {
        console.log(`[LOCAL DEV] ${fetcher.data.message || "App data reset successfully."}`);
      }
    }
  }, [fetcher.data, shopify]);

  const handleReset = () => {
    if (confirmText !== "DELETE") return;
    const formData = new FormData();
    formData.append("actionType", "RESET_APP_DATA");
    fetcher.submit(formData, { method: "post" });
  };

  return (
    <Box borderRadius="300">
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            Danger Zone
          </Text>
          {error && (
            <Banner tone="critical" title="Reset Failed">
              <p>{error}</p>
            </Banner>
          )}
          <Text as="p" variant="bodyMd">
            Manage your data. You can download a record of all {recordCount} return events or permanently reset your app state. 
            <br />
            {planName === STARTER_PLAN ? (
              <Text as="span" variant="bodySm" tone="critical">
                <b>Upgrade Required:</b> Data management features are exclusive to Growth, Pro and Enterprise plans.
              </Text>
            ) : (
              <Text as="span" variant="bodySm" tone="subdued">
                Tip: Download a CSV before resetting your history.
              </Text>
            )}
          </Text>
          {sessionShop && !isReadOnly && (
            <Box padding="200" background="bg-surface-secondary" borderRadius="200">
              <InlineStack align="space-between">
                <Text variant="bodyXs" tone="subdued" as="span">Identifier: {sessionShop}</Text>
                <Text variant="bodyXs" tone="subdued" as="span">Handle: {shopHandle}</Text>
              </InlineStack>
            </Box>
          )}
          <InlineStack align="end" gap="200">
            <div style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
              <Button 
                variant="secondary" 
                onClick={() => {
                  if (isReadOnly) {
                    const shopify = (window as any).shopify;
                    shopify?.toast.show("Download simulated! A complete data export would start here.", { duration: 3000 });
                    return;
                  }
                  onDownload();
                }} 
                loading={isDownloading}
                disabled={false}
              >
                Download CSV
              </Button>
            </div>
            <div className="dismiss-btn" style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
              <Button 
                variant="primary" 
                tone="critical"
                onClick={() => {
                  if (isReadOnly) {
                    const shopify = (window as any).shopify;
                    shopify?.toast.show("Data reset is locked in demo mode to protect the shared environment.", { duration: 3000, isError: true });
                    return;
                  }
                  setModalOpen(true);
                }} 
                disabled={false}
              >
                {planName === STARTER_PLAN ? "Upgrade for Reset" : "Reset History"}
              </Button>
            </div>
          </InlineStack>
        </BlockStack>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Permanently Reset App State?"
        primaryAction={{
          content: "Confirm Reset",
          onAction: handleReset,
          loading: isDeleting,
          disabled: confirmText !== "DELETE",
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="warning">
              This will wipe all recovered revenue stats and historical data. <b>This cannot be undone.</b>
            </Banner>
            <Text as="p">
              We strongly recommend downloading your historical data CSV before proceeding.
            </Text>
            <Text as="p">
              Please type <b>DELETE</b> below to confirm.
            </Text>
            <TextField
              label="Confirmation"
              labelHidden
              value={confirmText}
              onChange={setConfirmText}
              autoComplete="off"
              placeholder="DELETE"
              disabled={isDeleting}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Box>
  );
}
